import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import '@ant-design/compatible/assets/index.css';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
} from '../../thunks/problem_types_thunks';
import {
	fetchSupplierFacilitiesInPrivateNetworkForBuyer,
	fetchSupplierFacilitiesInPrivateNetworkForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import {
	workOrderPrioritiesRestCrudThunksForBuyer,
	workOrderPrioritiesRestCrudThunksForSupplier,
} from '../../thunks/work_order_priorities_thunks';

import { Row } from 'antd';
import {
	clearFiltersWorkOrders,
	fetchWorkOrders,
	updateFiltersWorkOrders,
	updatePaginationWorkOrders,
	updateSortingWorkOrders,
	replaceFiltersAndSortingWorkOrders,
} from '../../thunks/work_orders_thunks_wrapper';
import { getEntitiesByIdWithSearch, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import TableHeader from './TableHeader';
import WOTable from './WOTable';
import TableFooter from './TableFooter';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import { spendCategoriesRestCrudThunksForSupplier } from '../../thunks/spend_categories_thunks';
import { standardInitialStateGenerator } from '../../reducers/standard_reducer_utils';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
} from '../../thunks/asset_types_thunks';
import {
	resolutionTypesRestCrudThunksForBuyer,
	resolutionTypesRestCrudThunksForSupplier,
} from '../../thunks/resolution_types_thunks';
import {
	plannedMaintenanceSchedulesRestCrudThunksForBuyer,
	plannedMaintenanceSchedulesRestCrudThunksForSupplier,
} from '../../thunks/planned_maintenance_schedules_thunks';
import { ContractorPrivateNetworksRestCrudThunksForSupplier } from '../../thunks/contractors_private_networks_thunks';
import { fetchDistinctAreasHeirarchy } from '../../thunks/areas_thunks';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
} from '../../thunks/contractor_clients_thunks';
import {
	contractorLocationsRestCrudThunksForBuyer,
	contractorLocationsRestCrudThunksForSupplier,
} from '../../thunks/contractor_locations_thunks';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';
import {
	getContactsForThirdPartySuppliers,
	getContactsWithInternalTechs,
} from '../../thunks/contacts_thunks';
import {
	brandsRestCrudThunksForBuyer,
	brandsRestCrudThunksForSupplier,
} from '../../thunks/brands_thunks';
import { getWorkOrderFilterConfig } from '../../utils/WorkOrderUtils';
import { useFilters } from '../../hooks/useFilters';
import ExportMenu from '../export_modal/ExportMenu';

const TARGETCOLLECTIONNAME = 'workOrdersTable';

function WorkOrdersTable({
	data,
	openMoreInDefault,
	updatePagination,
	workOrders,
	workOrderSlice,
	fetchData,
	location,
	updateSorting,
	updateFilters,
	replaceFiltersAndSorting,
	clearFilters,
	companyConfig,
	currentUser,
	CustomFooter,
	CustomHeader,
	CustomHeaderProps = {},
	CustomFooterProps = {},
	CustomWorkOrderRowDisplay,
	CustomWorkOrderRowDisplayProps = {},
	tableStyle,
	history,
	renderEmptyState,
	fetchAreasDistinct,
	fetchLocations,
	fetchWorkOrderPriorities,
	fetchProblemTypes,
	preAppliedFilters,
	fetchSupplierFacilitiesInPrivateNetwork,
	fetchFieldTechs,
	fetchMultipleFieldTechs,
	filterConfig,
	locations,
	areas,
	fieldTechs,
	workOrderPriorities,
	problemTypes,
	supplierFacilities,
	userType,
	targetCollectionName,
	spendCategories,
	fetchSpendCategories,
	fetchMultipleSpendCategories,
	backText,
	assetTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	resolutionTypes,
	fetchResolutionTypes,
	fetchMultipleResolutionTypes,
	fetchPMSchedule,
	fetchMultiplePMSchedule,
	planned_maintenance_schedules,
	hideExportButton,
	rowSelection,
	simplifyRows,
	contractorPrivateNetworks,
	fetchContractors,
	contractorClients,
	fetchContractorClients,
	fetchMultipleContractorClients,
	contractorLocations,
	fetchContractorLocations,
	fetchMultipleContractorLocations,
	users,
	fetchUsers,
	fetchMultipleUsers,
	brands,
	fetchBrands,
	fetchMultipleBrands,
	onRefreshTable,
}) {
	const {
		fetching,
		filters: activeFilters,
		pagination,
		sorting: activeSorters,
	} = workOrders || standardInitialStateGenerator([targetCollectionName]);
	const exportMenu = hideExportButton
		? null
		: (trigger: React.ReactElement) => (
				<ExportMenu
					entityType="work_orders"
					title="Export Work Orders"
					userType={userType}
					filters={activeFilters}
					sorting={activeSorters}
				>
					{trigger}
				</ExportMenu>
		  );
	React.useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const preserve = queryParams.has('preserve');

		if (!preserve || !(data && data.length)) {
			fetchData(location);
		}
	}, []);

	const staleDays = useMemo(
		() => nullSafeGetOrElse('config.workOrder.stale.afterXDays', companyConfig, 14),
		[companyConfig]
	);

	const filters = useFilters({
		filterTargetCollectionName: 'workOrdersAdvancedFilters',
		overviewCollectionName: TARGETCOLLECTIONNAME,
		updateFilters: updateFilters,
	});

	const FILTER_CONFIG = useMemo(() => {
		const allFilters = getWorkOrderFilterConfig({
			filters,
			currentUser,
		});
		const filterOrder =
			filterConfig && filterConfig.filters && filterConfig.filters.length > 0
				? filterConfig.filters
				: null;
		return {
			...filterConfig,
			filters: filterOrder
				? filterOrder.map((fieldName) => filters[fieldName]).filter(Boolean)
				: allFilters,
		};
	}, [filters, filterConfig, userType, currentUser]);

	const refreshTable = React.useCallback(
		(location) => {
			if (onRefreshTable) {
				return onRefreshTable(location);
			}
			return fetchData(location);
		},
		[onRefreshTable, fetchData]
	);

	return (
		<div className="TableHoc">
			{CustomHeader ? (
				<CustomHeader
					{...CustomHeaderProps}
					applySorting={updateSorting}
					applyFilters={(e) => updateFilters(e)}
					filters={activeFilters}
					sorters={activeSorters}
					currentUser={currentUser}
					isDisplayModeActive={(e) => e === 'map'}
					exportMenu={exportMenu}
					counts={nullSafeGetOrElse(`${targetCollectionName}.counts`, workOrderSlice, {})}
					clearFilters={clearFilters}
					preAppliedFilters={preAppliedFilters}
					filterConfig={FILTER_CONFIG}
					targetCollectionName={targetCollectionName}
					userType={userType}
					pagination={pagination}
					fetching={fetching}
				/>
			) : (
				<TableHeader
					applySorting={updateSorting}
					applyFilters={(e) => updateFilters(e)}
					filters={activeFilters}
					sorters={activeSorters}
					currentUser={currentUser}
					isDisplayModeActive={(e) => e === 'map'}
					switchDisplayMode={(e) => {}}
					exportMenu={exportMenu}
					counts={nullSafeGetOrElse(`${targetCollectionName}.counts`, workOrderSlice, {})}
					clearFilters={clearFilters}
					filterConfig={FILTER_CONFIG}
					targetCollectionName={targetCollectionName}
					preAppliedFilters={preAppliedFilters}
					applyFiltersFromView={replaceFiltersAndSorting}
					userType={userType}
					companyConfig={companyConfig}
					refreshTable={refreshTable}
					location={location}
				/>
			)}

			<div style={{ marginTop: 16, background: 'white' }}>
				<WOTable
					fetching={fetching}
					data={data}
					currentUser={currentUser}
					companyConfig={companyConfig}
					CustomWorkOrderRowDisplay={CustomWorkOrderRowDisplay}
					CustomWorkOrderRowDisplayProps={CustomWorkOrderRowDisplayProps}
					refreshTable={refreshTable}
					tableStyle={tableStyle}
					renderEmptyState={renderEmptyState}
					userType={userType}
					location={location}
					backText={backText}
					targetCollectionName={targetCollectionName}
					rowSelection={rowSelection}
					simplifyRows={simplifyRows}
				/>
				{CustomFooter ? (
					<CustomFooter
						{...CustomFooterProps}
						updatePagination={updatePagination}
						pagination={{ ...pagination, current: 1, pageSize: 3 }}
						filters={activeFilters}
						sorters={activeSorters}
						location={location}
						history={history}
						userType={userType}
						openMoreInDefault={openMoreInDefault}
						currentUser={currentUser}
					/>
				) : (
					<Row style={{ padding: 16 }} justify="end">
						<TableFooter
							updatePagination={updatePagination}
							pagination={pagination}
							userType={userType}
						/>
					</Row>
				)}
			</div>
		</div>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	handleQueryParams: ownProps.handleQueryParams,
	locations: state.locations,
	currentUser: state.session.currentUser,
	workOrderPriorities: state.work_order_priorities,
	assetTypes: state.asset_types,
	contractorPrivateNetworks: state.contractor_private_networks,
	spendCategories: state.spend_categories,
	problemTypes: state.problem_types,
	regions: state.regions,
	areas: state.areas,
	resolutionTypes: state.resolution_types,
	supplierFacilities: state.supplier_facilities,
	fieldTechs: state.field_techs,
	planned_maintenance_schedules: state.planned_maintenance_schedules,
	workOrders: state.work_orders[ownProps.targetCollectionName || TARGETCOLLECTIONNAME],
	workOrderSlice: state.work_orders,
	companyConfig: state.company_config.detail,
	targetCollectionName: ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
	data: getEntitiesByIdWithSearch(
		state.work_orders[ownProps.targetCollectionName || TARGETCOLLECTIONNAME]
			? state.work_orders[ownProps.targetCollectionName || TARGETCOLLECTIONNAME].recordsIds || []
			: [],
		state.work_orders
	),
	contractorClients: state.contractor_clients,
	contractorLocations: state.contractor_locations,
	users: state.all_contacts,
	brands: state.brands,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchData: (location) =>
		dispatch(
			fetchWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				location,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination,
				ownProps.initialSorting,
				ownProps.initialFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	updatePagination: (pagination) =>
		dispatch(
			updatePaginationWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				ownProps.ignoreRefreshCounts
			)
		),
	updateSorting: (sorting) =>
		dispatch(
			updateSortingWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				sorting,
				true
			)
		),
	updateFilters: (filters) =>
		dispatch(
			updateFiltersWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				ownProps.ignoreRefreshCounts
			)
		),
	replaceFiltersAndSorting: (filters, sorting) =>
		dispatch(
			replaceFiltersAndSortingWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				sorting,
				ownProps.ignoreRefreshCounts
			)
		),
	clearFilters: () =>
		dispatch(
			clearFiltersWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	fetchWorkOrderPriorities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier
				: workOrderPrioritiesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier
				: assetTypesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultipleAssetTypes: (ids, targetCollectionName) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier
				: assetTypesRestCrudThunksForBuyer;
		return dispatch(fnToCall.readMultiple(ids, targetCollectionName));
	},
	fetchMultipleResolutionTypes: (ids, targetCollectionName) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? resolutionTypesRestCrudThunksForSupplier
				: resolutionTypesRestCrudThunksForBuyer;
		return dispatch(fnToCall.readMultiple(ids, targetCollectionName));
	},
	fetchResolutionTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? resolutionTypesRestCrudThunksForSupplier
				: resolutionTypesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier
				: problemTypesHeirarchicalForBuyer;
		return dispatch(
			fnToCall(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchSupplierFacilitiesInPrivateNetwork: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchSupplierFacilitiesInPrivateNetworkForSupplier
				: fetchSupplierFacilitiesInPrivateNetworkForBuyer;
		return dispatch(
			fnToCall(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchPMSchedule: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? plannedMaintenanceSchedulesRestCrudThunksForSupplier
				: plannedMaintenanceSchedulesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultiplePMSchedule: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? plannedMaintenanceSchedulesRestCrudThunksForSupplier
				: plannedMaintenanceSchedulesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.readMultiple(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultipleFieldTechs: (ids, targetCollectionName) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchSpendCategories: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			spendCategoriesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleSpendCategories: (ids, targetCollectionName) =>
		dispatch(spendCategoriesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchContractors: (params, targetCollectionName) =>
		dispatch(ContractorPrivateNetworksRestCrudThunksForSupplier.read(params, targetCollectionName)),
	fetchAreasDistinct: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fetchDistinctAreasHeirarchy(ownProps.userType)(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),

	fetchContractorClients: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: contractorClientsRestCrudThunksForBuyer.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleContractorClients: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: contractorClientsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchContractorLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: contractorLocationsRestCrudThunksForBuyer.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleContractorLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: contractorLocationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchUsers: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			isBuyerOrInternalTech(ownProps.currentUser)
				? getContactsWithInternalTechs(ownProps.userType).read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: getContactsForThirdPartySuppliers.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleUsers: (emails, targetCollectionName) =>
		dispatch(
			isBuyerOrInternalTech(ownProps.currentUser)
				? getContactsWithInternalTechs(ownProps.userType).read({ emails }, targetCollectionName)
				: getContactsForThirdPartySuppliers.read({ emails }, targetCollectionName)
		),
	fetchBrands: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandsRestCrudThunksForSupplier.read
				: brandsRestCrudThunksForBuyer.read;
		return dispatch(
			fnToCall(params, targetCollectionName, pagination, sorting, filters, addToTargetCollection)
		);
	},
	fetchMultipleBrands: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: brandsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
});

const WorkOrderHOC = withRouter(connect(mapStateToProps, mapDispatchToProps)(WorkOrdersTable));

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	(dispatch, ownProps) => ({})
)(WorkOrderHOC);
